import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import LoaderRombo from "../../../../components/Common/LoaderRombo";
import { CAROUSEL_TEXTS } from "../../../../constants/utils";
import { bulkTaskStatusUpdate, bulkSummaryGet } from "../../../../store/bulk-schedule/actions";
import { getBulkScheduleSummary } from "../../../../helpers/api_rombo_helper";
import i18n from "../../../../i18n";

const selectBulk = createSelector(
  (state) => state.BulkSchedule,
  (bs) => ({ taskId: bs.taskId, summary: bs.summary })
);
const selectToken = createSelector((state) => state.Login, (l) => l.token);

const POLL_INTERVAL = 5000;
const CAROUSEL_INTERVAL = 3000;

const StepValidating = () => {
  const dispatch = useDispatch();
  const { taskId, summary } = useSelector(selectBulk);
  const token = useSelector(selectToken);
  const esRef = useRef(null);
  const pollRef = useRef(null);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const currentLanguage = i18n.language === 'en' ? 'en' : 'es';
  const carouselTexts = CAROUSEL_TEXTS[currentLanguage];

  const handleEvent = (status, errors) => {
    dispatch(bulkTaskStatusUpdate(status, errors ?? []));
  };

  useEffect(() => {
    if (summary?.status === "success") {
      handleEvent("confirmed", []);
      return;
    }
  }, [summary]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex((prevIndex) => (prevIndex + 1) % carouselTexts.length);
    }, CAROUSEL_INTERVAL);

    return () => clearInterval(interval);
  }, [carouselTexts.length]);

  useEffect(() => {
    if (!taskId) return;

    const authToken = token?.Authorization ?? token?.authorization ?? "";
    const sseUrl = `/api/v2/taxi/bulk-schedule/sse/tasks/${taskId}/notifications${
      authToken ? `?token=${encodeURIComponent(authToken)}` : ""
    }`;

    if (typeof EventSource !== "undefined") {
      const es = new EventSource(sseUrl);
      esRef.current = es;

      es.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.status === "error") {
            handleEvent("error", data.errors ?? []);
            es.close();
          } else if (data.status === "confirmed" || data.status === "done") {
            handleEvent("confirmed", []);
            dispatch(bulkSummaryGet(taskId, "json"));
            es.close();
          }
        } catch (_) {}
      };

      es.onerror = () => {
        es.close();
        startPolling();
      };
    } else {
      startPolling();
    }

    const startPolling = () => {
      pollRef.current = setInterval(async () => {
        try {
          const res = await getBulkScheduleSummary(token, taskId, "json");
          if (res?.status === "error") {
            clearInterval(pollRef.current);
            handleEvent("error", res.errors ?? []);
          } else if (res?.status === "confirmed" || res?.status === "done") {
            clearInterval(pollRef.current);
            handleEvent("confirmed", []);
            dispatch(bulkSummaryGet(taskId, "json"));
          }
        } catch (_) {}
      }, POLL_INTERVAL);
    };

    return () => {
      esRef.current?.close();
      clearInterval(pollRef.current);
    };
  }, [taskId]);

  return (
    <div className="text-center py-5">
      <LoaderRombo/>
      <h5 className="mt-4">{i18n.t("ValidatingFile")}</h5>
      <p className="text-muted">{i18n.t("ThisMayTakeFewSeconds")}</p>
      <p className="text-muted carousel-text" key={currentTextIndex}>
        {carouselTexts[currentTextIndex]}
      </p>
    </div>
  );
};

export default StepValidating;
